import { Divider, Group, Paper, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import type { UseBaseMutationResult } from '@tanstack/react-query';
import { isArray } from 'lodash';
import { User } from 'oidc-client-ts';
import * as React from 'react';
import { For } from '../../../../react-commons/components/control-flow/For';
import { LabelNode } from '../../../../react-commons/components/inputs/getLabelNode';
import { ValidationEntrySeverity } from '../../../../react-commons/components/ValidationEntrySeverity';
import { assertUnreachable, formatIdentifier, formatToPascalCase } from '../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../resource/enumerations/ResourceType';
import type { NamedArtifactOutline } from '../../resource/outlines/basicOutline';
import type { UpdateMutateObject } from '../../resource/resource-base/mutateObjects';
import {
    getDefaultPersistableInfo,
    type PersistableResource,
} from '../../resource/resource-base/persistableResourceTypes';
import type { PropertyDetailsResource } from '../../resource/resources/propertyDetailsResource';
import type { MutationValidationResource } from '../../resource/resources/validation/mutationValidationResource';
import type { ValidationEntryDetailsResource } from '../../resource/resources/validation/validationEntryDetailsResource';
import type { PropertyValidation } from '../../resource/services/outlineTypes';

export function LightenDarkenColor(color: string, amount: number) {
    // #FFF not supportet rather use #FFFFFF
    const clamp = (val: number) => Math.min(Math.max(val, 0), 0xff);
    const fill = (str: string) => ('00' + str).slice(-2);

    const num = parseInt(color.substr(1), 16);
    const red = clamp((num >> 16) + amount);
    const green = clamp(((num >> 8) & 0x00ff) + amount);
    const blue = clamp((num & 0x0000ff) + amount);
    return '#' + fill(red.toString(16)) + fill(green.toString(16)) + fill(blue.toString(16));
}

export function isOIDC() {
    const isOIDC = localStorage.getItem(`isOIDC`);
    return isOIDC?.toLowerCase() === 'true';
}

export function getUser() {
    if (!isOIDC()) {
        return null;
    }
    const ssoUrl = localStorage.getItem(`OIDC_CLIENT_URL`);
    const ssoClientId = localStorage.getItem(`OIDC_CLIENT_ID`);
    const oidcStorage = sessionStorage.getItem(`oidc.user:${ssoUrl}:${ssoClientId}`);
    if (!oidcStorage) {
        return null;
    }

    return User.fromStorageString(oidcStorage);
}

export const changeStateBase = <
    P extends PersistableResource | Array<PersistableResource>,
    PO extends PersistableResource | Array<PersistableResource>,
>(
    newState: P,
    currentState: P,
    queryState: P | null | undefined,
    setState: (
        newState: P
    ) => void | React.Dispatch<React.SetStateAction<P>> | React.Dispatch<React.SetStateAction<P | undefined>>,
    onChanged: (dirty: boolean) => void,
    currentOtherState?: PO,
    queryOtherState?: PO | null
) => {
    const replacer = (key: string, value: any) => (key === 'resourceType' ? undefined : value);

    const newStateJSON = JSON.stringify(newState, replacer);
    const changed = newStateJSON !== JSON.stringify(currentState, replacer);
    if (changed) {
        setState(newState);
        const dirty =
            newStateJSON !== JSON.stringify(queryState, replacer) ||
            JSON.stringify(currentOtherState, replacer) !== JSON.stringify(queryOtherState, replacer);
        onChanged(dirty);
    }
};

export const getPropertyTableTitle = (
    propertyDetails: Record<string, PropertyDetailsResource> | undefined,
    identifer: string,
    labelOverride?: string
) => {
    if (propertyDetails?.[identifer]) {
        const propDetails = propertyDetails[identifer];
        const text =
            propDetails ?
                propDetails?.displayName ?
                    (labelOverride ?? propDetails.displayName)
                :   formatIdentifier(labelOverride ?? propDetails.propertyName ?? '')
            :   (formatIdentifier(labelOverride ?? identifer) ?? '');
        const typography = (
            <Text style={{ lineHeight: 1.8 }} size={'sm'} c={'var(--mantine-text-reverse-secondary-color)'}>
                {text}
            </Text>
        );
        return <LabelNode label={typography} labelInfo={getPropertyLabelInfo(propDetails)} />;
    }
    return formatIdentifier(labelOverride ?? identifer);
};

export const getPropertyTableLabel = (
    propertyDetails: Record<string, PropertyDetailsResource> | undefined,
    identifer: string | Array<string>,
    labelOverride?: string
) => {
    if (isArray(identifer)) {
        if (propertyDetails?.[0]) {
            const propDetails = identifer.map((value) => propertyDetails[value]).filter((value) => value !== undefined);
            return (
                <LabelNode
                    label={
                        propertyDetails[0].displayName ?
                            (labelOverride ?? propertyDetails[0]?.displayName)
                        :   formatIdentifier(labelOverride ?? propertyDetails[0].propertyName ?? '')
                    }
                    labelInfo={getPropertyLabelInfo(propDetails)}
                />
            );
        }
        return formatIdentifier(labelOverride ?? identifer[0]);
    } else {
        if (propertyDetails?.[identifer]) {
            const propDetails = propertyDetails[identifer];
            return (
                <LabelNode
                    label={
                        propDetails ?
                            propDetails?.displayName ?
                                (labelOverride ?? propDetails.displayName)
                            :   formatIdentifier(labelOverride ?? propDetails.propertyName ?? '')
                        :   (formatIdentifier(labelOverride ?? identifer) ?? '')
                    }
                    labelInfo={getPropertyLabelInfo(propDetails)}
                />
            );
        }
        return formatIdentifier(labelOverride ?? identifer);
    }
};

export const getPropertyLabelInfo = (
    propertyDetails: Array<PropertyDetailsResource> | PropertyDetailsResource | undefined
) => {
    if (isArray(propertyDetails)) {
        return (
            <div style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                <Text c={'var(--mantine-text-reverse-secondary-color)'}>Multiple Possible Properties</Text>
                <Divider orientation={'horizontal'} />
                <For each={propertyDetails} keyFn={(item) => item.propertyName}>
                    {(item) => (
                        <Paper bg={'var(--tooltip-bg)'} withBorder={true} p={'md'}>
                            {getPropertyLabelInfo(item)}
                        </Paper>
                    )}
                </For>
            </div>
        );
    } else if (propertyDetails?.description) {
        return (
            <div style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                <Text c={'var(--mantine-text-reverse-secondary-color)'}>{propertyDetails.displayName}</Text>
                <Divider orientation={'horizontal'} />
                <div style={{ margin: '6px 0 6px 0' }} />
                <Text c={'var(--mantine-text-reverse-secondary-color)'} size={'sm'} style={{ whiteSpace: 'pre-wrap' }}>
                    {propertyDetails.description}
                </Text>
                <div style={{ margin: '6px 0 6px 0' }} />
                <Divider orientation={'horizontal'} />
                <Group py={6}>
                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>functional</Text>
                    {propertyDetails.isFunctional ?
                        <IconCheck
                            size={20}
                            style={{ paddingTop: 3 }}
                            color={'var(--mantine-color-primaryGreen-filled)'}
                        />
                    :   <IconX size={20} style={{ paddingTop: 3 }} color={'var(--mantine-color-error)'} />}
                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>updatable</Text>
                    {propertyDetails.updatable ?
                        <IconCheck
                            size={20}
                            style={{ paddingTop: 3 }}
                            color={'var(--mantine-color-primaryGreen-filled)'}
                        />
                    :   <IconX size={20} style={{ paddingTop: 3 }} color={'var(--mantine-color-error)'} />}
                </Group>
            </div>
        );
    } else if (propertyDetails) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {propertyDetails.displayName && (
                    <>
                        <Text c={'var(--mantine-text-reverse-secondary-color)'}>{propertyDetails.displayName}</Text>
                        <Divider orientation={'horizontal'} />
                    </>
                )}
                <Group py={6}>
                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>functional</Text>
                    {propertyDetails.isFunctional ?
                        <IconCheck
                            size={20}
                            style={{ paddingTop: 3 }}
                            color={'var(--mantine-color-primaryGreen-filled)'}
                        />
                    :   <IconX size={20} style={{ paddingTop: 3 }} color={'var(--mantine-color-error)'} />}
                    <Text c={'var(--mantine-text-reverse-secondary-color)'}>updatable</Text>
                    {propertyDetails.updatable ?
                        <IconCheck
                            size={20}
                            style={{ paddingTop: 3 }}
                            color={'var(--mantine-color-primaryGreen-filled)'}
                        />
                    :   <IconX size={20} style={{ paddingTop: 3 }} color={'var(--mantine-color-error)'} />}
                </Group>
            </div>
        );
    }
    return undefined;
};

export const getFormattedPropertyValidation = (
    property: string | Array<string>,
    propertyValidationMapping: Record<string, { severity: ValidationEntrySeverity; identifier: string }> | undefined,
    propertyDetail: Record<string, ValidationEntryDetailsResource> | undefined
): PropertyValidation | null => {
    const propValidation =
        propertyValidationMapping ?
            isArray(property) ?
                property.map((value) => propertyValidationMapping[value]).find((value) => value !== undefined)
            :   propertyValidationMapping[property]
        :   null;
    if (propValidation && propertyDetail) {
        const detail = propertyDetail[propValidation.identifier];
        if (detail && detail.displayName !== '') {
            return { identifier: detail.displayName, severity: propValidation.severity };
        }
    }
    return propValidation ?
            { identifier: formatIdentifier(propValidation.identifier), severity: propValidation.severity }
        :   (propValidation ?? null);
};

export async function handleVariantedEditSaveClick<P extends PersistableResource, V extends PersistableResource>(
    persistableState: P,
    persistableQueryData: P | null | undefined,
    persistableUpdateFunction: UseBaseMutationResult<MutationValidationResource | null, unknown, UpdateMutateObject<P>>,
    variantState: V,
    variantQueryData: V | null | undefined,
    variantUpdateFunction: UseBaseMutationResult<MutationValidationResource | null, unknown, UpdateMutateObject<V>>,
    dispatchDirty?: () => void
) {
    let persistableUpdateSuccessful = true;
    if (Boolean(persistableState.id) && JSON.stringify(persistableState) !== JSON.stringify(persistableQueryData)) {
        const result = await persistableUpdateFunction.mutateAsync({
            resource: persistableState,
            id: persistableState.id,
        });
        persistableUpdateSuccessful = Boolean(result?.successful);
    }

    let variantUpdateSuccessful = true;
    if (Boolean(variantState.id) && JSON.stringify(variantState) !== JSON.stringify(variantQueryData)) {
        const result = await variantUpdateFunction.mutateAsync({
            resource: variantState,
            id: variantState.id,
        });
        variantUpdateSuccessful = Boolean(result?.successful);
    }

    if (dispatchDirty && (!persistableUpdateSuccessful || !variantUpdateSuccessful)) {
        dispatchDirty();
    }

    return persistableUpdateSuccessful && variantUpdateSuccessful;
}

type UndefinableResource<R extends PersistableResource> = R | undefined | null;

export const compareResources = <R extends PersistableResource>(
    resource1: UndefinableResource<R>,
    resource2: UndefinableResource<R>
): boolean => {
    const getComparablePersistable = (persistable: PersistableResource): PersistableResource => ({
        id: persistable.id,
        resourceType: persistable.resourceType,
        persistableInfo: getDefaultPersistableInfo(),
    });
    const nullOutNecessary = (res: UndefinableResource<R>): UndefinableResource<R> => {
        if (!res) {
            return res;
        }
        return {
            ...res,
            ...getComparablePersistable(res),
        };
    };
    return JSON.stringify(nullOutNecessary(resource1)) === JSON.stringify(nullOutNecessary(resource2));
};

export const getResourceTypeShortLabel = (resourceType: ResourceType): string => {
    const label: string = resourceType;
    return formatIdentifier(label);
};

export const compareAlphanumerically = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

export const handleMutationResult = (onSuccess: (result: MutationValidationResource) => void) => {
    return (result: MutationValidationResource | null) => {
        if (result?.successful) {
            onSuccess(result);
        }
    };
};

// TODO creationDialogWithDefinitionOnSuccess
// TODO creationDialogResetOnSuccess
export const creationDialogOnSuccess = (
    onCreated: ((id: string, objectId: string) => void) | undefined,
    onClose: () => void,
    setChanged?: React.Dispatch<React.SetStateAction<boolean>>
) =>
    handleMutationResult((value) => {
        if (onCreated && value.content?.objectId) {
            onCreated(value.content.id, value.content.objectId);
        }
        setChanged?.(false);
        onClose();
    });

export const creationDialogWithNameOnSuccess = (
    onCreated: ((id: string, objectId: string, name: string) => void) | undefined,
    onClose: () => void,
    reset: () => void,
    data: { name: string }
) =>
    handleMutationResult((value) => {
        if (onCreated && value.content?.objectId) {
            onCreated(value.content.id, value.content.objectId, value.content.name ?? data.name);
        }
        reset();
        onClose();
    });

export const compareValidationSeverity = (
    severity1: ValidationEntrySeverity,
    severity2: ValidationEntrySeverity
): number => {
    if (severity1 === severity2) {
        return 0;
    }
    switch (severity1) {
        case ValidationEntrySeverity.FATAL: {
            return -1;
        }
        case ValidationEntrySeverity.CRITICAL: {
            if (severity2 === ValidationEntrySeverity.FATAL) {
                return 1;
            } else {
                return -1;
            }
        }
        case ValidationEntrySeverity.WARNING: {
            if (severity2 === ValidationEntrySeverity.NOTE) {
                return -1;
            } else {
                return 1;
            }
        }
        case ValidationEntrySeverity.NOTE: {
            return 1;
        }
        default: {
            return assertUnreachable(severity1);
        }
    }
};

export const getUpdatedResource = <T extends PersistableResource, Key extends keyof T>(
    resource: T,
    updateOn: Key,
    newValue: T[Key],
    updateDependentProperties?: (t: T) => T
): T => {
    const updatedResource: T = {
        ...resource,
        [updateOn]: newValue,
    };
    return updateDependentProperties ? updateDependentProperties(updatedResource) : updatedResource;
};

export const mapNotNull = <A extends { id: string | null; objectId: string | null; name: string }>(
    array: Array<A>
): Array<NamedArtifactOutline> => {
    const newArray: Array<NamedArtifactOutline> = [];

    array.forEach((value) => {
        if (value.id !== null && value.objectId !== null) {
            newArray.push({
                id: value.id,
                objectId: value.objectId,
                name: value.name,
            });
        }
    });

    return newArray;
};

export const betterMathDotRandom = () => {
    const randomBuffer = new Uint32Array(1);
    window.crypto.getRandomValues(randomBuffer);
    return randomBuffer[0] / (0xffffffff + 1); // Normalize to range [0,  1)
};

export const groupByFormatted = <R extends object /*, E extends string*/>(
    groupBy: ((resource: R) => string) | undefined
    // _enumType?: Enum<E>
) => {
    // TODO - enumType for formatEnum
    return groupBy?.let((it) => (resource: R) => formatToPascalCase(it(resource)));
};
